import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, CheckCircle2, ClipboardPlus, Clock3, FileCheck2, Trash2 } from "lucide-react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PageHeader, PageHeaderChip } from "@/components/page/page-header";
import { RbimDemographicPagesStep } from "@/modules/add-new-data/components/rbim-demographic-pages-step";
import { RbimEconomicHealthPagesStep } from "@/modules/add-new-data/components/rbim-economic-health-pages-step";
import { RbimFrontPageStep } from "@/modules/add-new-data/components/rbim-front-page-step";
import { RbimHealthMigrationPagesStep } from "@/modules/add-new-data/components/rbim-health-migration-pages-step";
import { RbimHouseholdQuestionsStep } from "@/modules/add-new-data/components/rbim-household-questions-step";
import { RbimMigrationSkillsPagesStep } from "@/modules/add-new-data/components/rbim-migration-skills-pages-step";
import { ReviewSubmitStep } from "@/modules/add-new-data/components/review-submit-step";
import {
  applySkipPattern,
  demographicSkipFields,
  economicHealthSkipFields,
  healthMigrationSkipFields,
  migrationSkillsSkipFields,
  type MemberFieldName,
} from "@/modules/add-new-data/lib/cleansing";
import {
  getAgeReferenceDateValue,
  parseMMDDYYYY,
  validateDOB,
} from "@/modules/add-new-data/lib/age";
import {
  addNewDataSchema,
  AddNewDataFormValues,
  defaultAddNewDataValues,
  emptyMember,
  initializeAddNewDataValues,
} from "@/modules/add-new-data/lib/schema";
import { createMissingHouseholdMemberRows } from "@/modules/add-new-data/lib/household-member-rows";
import {
  composeResidence,
  deriveResidentTypeCode,
  isLengthOfStayAtLeastFiveYears,
  shouldClearAutomatedResidenceValue,
} from "@/modules/add-new-data/lib/questionnaire-split-values";
import {
  saveRbimSubmission,
  saveRbimSubmissionUpdate,
} from "@/modules/add-new-data/lib/submission-persistence";
import {
  validateCurrentStep,
  type CurrentStepValidationError,
} from "@/modules/add-new-data/lib/validation";
import { useAddNewDataDraftStore } from "@/modules/add-new-data/stores/add-new-data-draft-store";
import { cn } from "@/lib/utils";
import { useSessionStore } from "@/stores/session-store";
import { useSettingsStore, type BarangayLocationSettings } from "@/stores/settings-store";
import {
  getRbimSubmissionEditContext,
  type RbimSubmissionEditContext,
} from "@/lib/rbim-storage";

const steps = [
  {
    id: 0,
    title: "Front Page",
    description: "Identification, interview, encoding",
  },
  {
    id: 1,
    title: "Pages 2 - 3",
    description: "Household members",
  },
  {
    id: 2,
    title: "Pages 4 - 5",
    description: "Resident details continuation",
  },
  {
    id: 3,
    title: "Pages 6 - 7",
    description: "Health, civic, migration",
  },
  {
    id: 4,
    title: "Pages 8 - 9",
    description: "Migration, CTC, skills",
  },
  {
    id: 5,
    title: "Pages 10 - 11",
    description: "Household questions",
  },
  {
    id: 6,
    title: "Review",
    description: "Final review",
  },
] as const;

const TIME_END_SUBMISSION_MESSAGE =
  "Please enter the Time End on the Front Page before submitting.";

const RESULT_SUBMISSION_MESSAGE =
  "Please enter the Result on the Front Page before submitting.";

const TIME_END_AND_RESULT_SUBMISSION_MESSAGE =
  "Please complete the Time End and Result on the Front Page before submitting.";

const PRIMARY_TIME_END_PATH = "interviews.0.timeEnd";
const PRIMARY_RESULT_PATH = "interviews.0.result";

export function AddNewDataPage() {
  const [searchParams] = useSearchParams();
  const editSubmissionId = searchParams.get("editSubmissionId")?.trim() ?? "";
  const editResidentId = searchParams.get("residentId")?.trim() ?? "";
  const editScope =
    searchParams.get("editScope") === "household"
      ? "household"
      : "individual";
  const isEditMode = Boolean(editSubmissionId && editResidentId);
  const isHouseholdEditMode = isEditMode && editScope === "household";
  const isIndividualEditMode = isEditMode && !isHouseholdEditMode;
  const [currentStep, setCurrentStep] = useState(0);
  const [confirmClearDraft, setConfirmClearDraft] = useState(false);
  const [submitPreviewReady, setSubmitPreviewReady] = useState(false);
  const [isSubmittingRecord, setIsSubmittingRecord] = useState(false);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [submissionStorage, setSubmissionStorage] = useState<"sqlite" | "browser-fallback" | null>(null);
  const [submissionWarning, setSubmissionWarning] = useState<string | null>(null);
  const [stepValidationSummary, setStepValidationSummary] = useState<string | null>(null);
  const [editContext, setEditContext] = useState<RbimSubmissionEditContext | null>(null);
  const [isEditLoading, setIsEditLoading] = useState(isEditMode);
  const [editLoadError, setEditLoadError] = useState<string | null>(null);
  const didRestoreDraft = useRef(false);
  const draft = useAddNewDataDraftStore((state) => state.draft);
  const lastSavedAt = useAddNewDataDraftStore((state) => state.lastSavedAt);
  const clearDraft = useAddNewDataDraftStore((state) => state.clearDraft);
  const saveDraft = useAddNewDataDraftStore((state) => state.saveDraft);
  const setHasUnsavedDraft = useSessionStore((state) => state.setHasUnsavedDraft);
  const location = useSettingsStore((state) => state.location);
  const locationLocked = useSettingsStore((state) => state.locationLocked);
  const defaultLocation = hasSavedLocation(location) ? location : null;
  const newRecordValuesRef = useRef<AddNewDataFormValues | null>(null);
  const newRecordValues = newRecordValuesRef.current ?? initializeAddNewDataValues();
  newRecordValuesRef.current = newRecordValues;

  const initialValues = useMemo(
    () => {
      const values = draft && !isEditMode
        ? cloneFormValues(initializeAddNewDataValues(draft))
        : cloneFormValues(newRecordValues);

      return isEditMode
        ? values
        : applySettingsLocation(values, defaultLocation, !draft || locationLocked);
    },
    [defaultLocation, draft, isEditMode, locationLocked, newRecordValues],
  );

  const form = useForm<AddNewDataFormValues>({
    resolver: zodResolver(addNewDataSchema),
    defaultValues: initialValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const {
    control,
    formState: { errors, isDirty, submitCount, touchedFields },
    getValues,
    handleSubmit,
    register,
    reset,
    resetField,
    clearErrors,
    setError,
    setFocus,
    setValue,
    trigger,
  } = form;

  const { append, fields, remove } = useFieldArray({
    control,
    name: "members",
  });

  const watchedValues = useWatch({ control }) as AddNewDataFormValues;
  const watchedMembers = watchedValues.members ?? [];
  const currentValues = getCurrentValues(watchedValues, getValues());
  const ageReferenceDateValue = getAgeReferenceDateValue(currentValues.interviews, {
    members: currentValues.members,
  });

  useEffect(() => {
    if (!isEditMode) {
      setEditContext(null);
      setIsEditLoading(false);
      setEditLoadError(null);
      return;
    }

    let cancelled = false;
    setIsEditLoading(true);
    setEditLoadError(null);

    void getRbimSubmissionEditContext(editSubmissionId, editResidentId)
      .then((context) => {
        if (cancelled) {
          return;
        }

        setEditContext(context);
        reset(cloneFormValues(context.values));
        setCurrentStep(isHouseholdEditMode ? 0 : 1);
        setIsEditLoading(false);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        setEditLoadError(error instanceof Error ? error.message : String(error));
        setIsEditLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    editResidentId,
    editSubmissionId,
    isEditMode,
    isHouseholdEditMode,
    reset,
  ]);

  useEffect(() => {
    const missingMembers = createMissingHouseholdMemberRows(
      watchedValues.household?.totalMembers,
      fields.length,
      () => ({ ...emptyMember }),
    );

    if (missingMembers.length > 0) {
      append(missingMembers, { shouldFocus: false });
    }
  }, [append, fields.length, watchedValues.household?.totalMembers]);

  useEffect(() => {
    const ageReferenceDate = parseMMDDYYYY(ageReferenceDateValue) ?? new Date();

    watchedMembers.forEach((member, index) => {
      if (member.birthdateSource === "legacy-import") {
        return;
      }

      const isUnknown = member.dateOfBirthUnknown === true;
      const hasValidBirthdate = !isUnknown && validateDOB(member.dateOfBirth, ageReferenceDate).isValid;
      const nextAgeSource = isUnknown ? "reported" : hasValidBirthdate ? "date-of-birth" : null;
      const nextAgeAsOfDate = ageReferenceDateValue;
      const nextPrecision = isUnknown ? "age-only" : hasValidBirthdate ? "exact" : "unknown";

      if (member.ageSource !== nextAgeSource) {
        setValue(`members.${index}.ageSource`, nextAgeSource, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }

      if (member.ageAsOfDate !== nextAgeAsOfDate) {
        setValue(`members.${index}.ageAsOfDate`, nextAgeAsOfDate, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }

      if (member.birthdatePrecision !== nextPrecision) {
        setValue(`members.${index}.birthdatePrecision`, nextPrecision, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }

      if (nextAgeSource && member.birthdateSource !== "manual") {
        setValue(`members.${index}.birthdateSource`, "manual", {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    });
  }, [ageReferenceDateValue, setValue, watchedMembers]);

  useEffect(() => {
    if (isEditMode) {
      return;
    }

    if (draft && !didRestoreDraft.current && !isDirty) {
      reset(applySettingsLocation(
        cloneFormValues(initializeAddNewDataValues(draft)),
        defaultLocation,
        locationLocked,
      ));
      didRestoreDraft.current = true;
    }
  }, [defaultLocation, draft, isDirty, isEditMode, locationLocked, reset]);

  useEffect(() => {
    if (isEditMode) {
      return;
    }

    if (draft || isDirty) {
      return;
    }

    reset(applySettingsLocation(cloneFormValues(newRecordValues), defaultLocation, true));
  }, [defaultLocation, draft, isDirty, isEditMode, newRecordValues, reset]);

  useEffect(() => {
    if (isEditMode || !locationLocked || !defaultLocation) {
      return;
    }

    const currentHousehold = getValues().household;
    const lockedLocationFields: Array<[keyof BarangayLocationSettings, string]> = [
      ["province", "household.province"],
      ["provinceCode", "household.provinceCode"],
      ["municipality", "household.municipality"],
      ["municipalityCode", "household.municipalityCode"],
      ["barangay", "household.barangay"],
      ["barangayCode", "household.barangayCode"],
    ];

    lockedLocationFields.forEach(([locationField, formField]) => {
      if (currentHousehold[locationField] === defaultLocation[locationField]) {
        return;
      }

      setValue(formField as Parameters<typeof setValue>[0], defaultLocation[locationField] as never, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    });
  }, [defaultLocation, getValues, isEditMode, locationLocked, setValue]);

  useEffect(() => {
    if (isDirty) {
      setHasUnsavedDraft(true);
    }
  }, [isDirty, setHasUnsavedDraft, watchedValues]);

  useEffect(() => {
    if (isEditMode) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (!isDirty) {
        return;
      }

      saveDraft(cloneFormValues(getValues()));
      setHasUnsavedDraft(true);
    }, 10_000);

    return () => window.clearInterval(intervalId);
  }, [getValues, isDirty, isEditMode, saveDraft, setHasUnsavedDraft]);

  useEffect(() => {
    watchedMembers.forEach((member, index) => {
      const nextMember = applySkipPattern(member);
      const fieldsToSync: MemberFieldName[] = [
        ...demographicSkipFields,
        ...economicHealthSkipFields,
        ...healthMigrationSkipFields,
        ...migrationSkillsSkipFields,
        "reasonNotAttendingSchoolOther",
        "sourceOfIncomeOther",
        "placeOfDeliveryOther",
        "birthAttendantOther",
        "familyPlanningUseOther",
        "fpMethodSourceOther",
        "intentionToUseFpDetail",
        "healthInsuranceOther",
        "facilityVisitedPast12MonthsOther",
        "healthFacilityVisitReasonOther",
        "reasonLeavingPreviousResidence1Other",
        "reasonLeavingPreviousResidence2Other",
        "reasonLeavingPreviousResidence3Other",
        "returnToPreviousResidenceDetail",
        "transferReason1Other",
        "transferReason2Other",
        "transferReason3Other",
        "durationOfStayCurrentBarangayDetail",
        "skillsOther",
      ];

      fieldsToSync.forEach((field) => {
        if (member[field] === nextMember[field]) {
          return;
        }

        setValue(`members.${index}.${field}`, nextMember[field] ?? "", {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      });
    });
  }, [setValue, watchedMembers]);

  // Q34 drives Q35/Q36 current-location values and the canonical Q37 code.
  // Track which values were generated by this rule so manual values are not
  // accidentally cleared when Q34 becomes less than five years again.
  const autoFilledQ35Q36Ref = useRef<Map<string, string>>(new Map());
  const autoFilledQ37Ref = useRef<Map<number, string>>(new Map());

  const currentBarangay = String(watchedValues?.household?.barangay ?? "").trim();
  const currentMunicipality = String(watchedValues?.household?.municipality ?? "").trim();
  const currentResidenceForQ35Q36 =
    currentBarangay && currentMunicipality
      ? composeResidence({
          barangay: currentBarangay,
          cityMunicipality: currentMunicipality,
        })
      : "";

  useEffect(() => {
    if (!Array.isArray(watchedMembers)) {
      return;
    }

    const trackedValues = autoFilledQ35Q36Ref.current;

    const syncResidenceField = (
      index: number,
      member: AddNewDataFormValues["members"][number],
      field:
        | "previousResidenceFiveYears"
        | "previousResidenceSixMonths",
    ) => {
      const key = `${index}:${field}`;
      const currentValue = String(member[field] ?? "").trim();

      const shouldAutoFill =
        isLengthOfStayAtLeastFiveYears(member.lengthOfStayInBarangay) &&
        Boolean(currentResidenceForQ35Q36);

      if (shouldAutoFill) {
        if (currentValue !== currentResidenceForQ35Q36) {
          setValue(
            `members.${index}.${field}`,
            currentResidenceForQ35Q36,
            {
              shouldDirty: true,
              shouldTouch: false,
              shouldValidate: true,
            },
          );
        }

        trackedValues.set(key, currentResidenceForQ35Q36);
        return;
      }

      const autoFilledValue = trackedValues.get(key);

      if (autoFilledValue !== undefined) {
        // Clear it only if it still contains the value inserted by us.
        if (shouldClearAutomatedResidenceValue(currentValue, autoFilledValue)) {
          setValue(`members.${index}.${field}`, "", {
            shouldDirty: true,
            shouldTouch: false,
            shouldValidate: true,
          });
        }

        trackedValues.delete(key);
      }
    };

    watchedMembers.forEach((member, index) => {
      syncResidenceField(
        index,
        member,
        "previousResidenceFiveYears",
      );

      syncResidenceField(
        index,
        member,
        "previousResidenceSixMonths",
      );

      const derivedResidentType = deriveResidentTypeCode(
        member.lengthOfStayInBarangay,
      );
      const trackedResidentTypes = autoFilledQ37Ref.current;

      if (derivedResidentType) {
        if (member.residentType !== derivedResidentType) {
          setValue(`members.${index}.residentType`, derivedResidentType, {
            shouldDirty: true,
            shouldTouch: false,
            shouldValidate: true,
          });
        }

        trackedResidentTypes.set(index, derivedResidentType);
        return;
      }

      const previousDerivedResidentType = trackedResidentTypes.get(index);

      if (previousDerivedResidentType !== undefined) {
        if (member.residentType === previousDerivedResidentType) {
          setValue(`members.${index}.residentType`, "", {
            shouldDirty: true,
            shouldTouch: false,
            shouldValidate: true,
          });
        }

        trackedResidentTypes.delete(index);
      }
    });
  }, [
    currentResidenceForQ35Q36,
    setValue,
    watchedMembers,
  ]);

  const navigate = useNavigate();

  async function goToNextStep() {
    const isStepValid = await validateStep(currentStep);

    if (!isStepValid) {
      return;
    }

    const nextStep = clampStep(currentStep + 1);

    // Review is a final-submission stage. Never allow the user to enter it
    // until the first-visit Time End has been completed.
    if (
      nextStep === steps.length - 1 &&
      blockSubmissionForMissingInterviewCompletion(getValues())
    ) {
      return;
    }

    setStepValidationSummary(null);
    setCurrentStep(nextStep);
  }

  async function goToStep(targetStep: number) {
    const boundedTargetStep = clampStep(targetStep);

    // Prevent direct navigation to Review from bypassing the Front Page.
    if (
      boundedTargetStep === steps.length - 1 &&
      blockSubmissionForMissingInterviewCompletion(getValues())
    ) {
      return;
    }

    if (boundedTargetStep <= currentStep) {
      setStepValidationSummary(null);
      setCurrentStep(boundedTargetStep);
      return;
    }

    for (let step = currentStep; step < boundedTargetStep; step += 1) {
      const isStepValid = await validateStep(step);

      if (!isStepValid) {
        setCurrentStep(step);
        return;
      }
    }

    setCurrentStep(boundedTargetStep);
  }

  async function validateStep(step: number) {
    commitActiveFieldValue();
    clearErrors();

    const stepErrors = validateCurrentStep(step, getValues());

    if (stepErrors.length > 0) {
      showCurrentStepValidationErrors(stepErrors);
      return false;
    }

    applyCurrentStepSkipPatterns();

    if (step > 0) {
      setStepValidationSummary(null);
      return true;
    }

    /*
     * Front Page validation must validate only fields that actually
     * belong to the Front Page.
     *
     * Family B+ relationship to the Household Head is intentionally
     * answered on Pages 2-3, so it must not block navigation from
     * the Front Page.
     */
    const stepIsValid = await trigger(
      [
        "household.province",
        "household.provinceCode",
        "household.municipality",
        "household.municipalityCode",
        "household.barangay",
        "household.barangayCode",
        "household.purok",
        "household.address",
        "household.householdCode",
        "household.family",
        "household.householdType",
        "household.respondentName",
        "household.householdHead",
        "household.totalMembers",
      ],
      { shouldFocus: true },
    );

    if (!stepIsValid) {
      setStepValidationSummary("Please complete all required fields before proceeding.");
      return false;
    }

    setStepValidationSummary(null);
    return true;
  }

  function submitFromReview() {
    // Saving is permitted only after the user explicitly clicks Submit
    // while Step 7 - Review is currently displayed.
    if (currentStep !== steps.length - 1 || isSubmittingRecord) {
      return;
    }

    commitActiveFieldValue();

    if (blockSubmissionForMissingInterviewCompletion(getValues())) {
      return;
    }

    void handleSubmit(handlePreviewSubmit, handleInvalidSubmit)();
  }

  async function handlePreviewSubmit(_values: AddNewDataFormValues) {
    // Submission is allowed only from the Review step.
    // Next on Pages 10-11 must never save or navigate to Household Profiles.
    if (currentStep !== steps.length - 1) {
      return;
    }
    commitActiveFieldValue();

    if (blockSubmissionForMissingInterviewCompletion(getValues())) {
      return;
    }

    applyCurrentStepSkipPatterns();

    const normalizedValues = getNormalizedCurrentValues();
    const allStepErrors = getAllStepValidationErrors(normalizedValues);

    if (allStepErrors.length > 0) {
      showCurrentStepValidationErrors(allStepErrors);
      setCurrentStep(findStepForPath(allStepErrors[0].path));
      return;
    }

    if (!isEditMode) {
      saveDraft(normalizedValues);
      setHasUnsavedDraft(true);
    }
    setSubmitPreviewReady(false);
    setSubmissionId(null);
    setSubmissionStorage(null);
    setSubmissionWarning(null);
    setIsSubmittingRecord(true);

    try {
      const result = isEditMode
        ? await saveRbimSubmissionUpdate(
            editSubmissionId,
            normalizedValues,
            {
              scope: isHouseholdEditMode ? "household" : "individual",
            },
          )
        : await saveRbimSubmission(normalizedValues);

      setSubmissionId(result.id);
      setSubmissionStorage(result.storage);
      setSubmitPreviewReady(true);
      setHasUnsavedDraft(false);
      if (isEditMode) {
        navigate(
          isHouseholdEditMode ? "/households" : "/residents",
          { replace: true },
        );
      } else {
        const nextRecordValues = initializeAddNewDataValues();
        newRecordValuesRef.current = nextRecordValues;
        reset(applySettingsLocation(nextRecordValues, defaultLocation, true));
        clearDraft();
        navigate("/households", { replace: true });
      }
    } catch (error) {
      setSubmissionWarning(
        error instanceof Error
          ? error.message
          : "The cleaned submission could not be saved to SQLite in this runtime.",
      );
      setSubmitPreviewReady(false);
    } finally {
      setIsSubmittingRecord(false);
    }
  }

  function handleInvalidSubmit() {
    commitActiveFieldValue();

    if (blockSubmissionForMissingInterviewCompletion(getValues())) {
      return;
    }

    applyCurrentStepSkipPatterns();

    const allStepErrors = getAllStepValidationErrors(getNormalizedCurrentValues());

    if (allStepErrors.length > 0) {
      showCurrentStepValidationErrors(allStepErrors);
      setCurrentStep(findStepForPath(allStepErrors[0].path));
      return;
    }

    setStepValidationSummary("Please complete all required fields before proceeding.");
    focusFirstInvalidField();
  }

  function blockSubmissionForMissingInterviewCompletion(values: AddNewDataFormValues) {
    const timeEndMissing =
      !isValidSubmissionTimeEnd(values.interviews?.[0]?.timeEnd);

    const resultMissing =
      !hasSubmissionResult(values.interviews?.[0]?.result);

    if (!timeEndMissing && !resultMissing) {
      return false;
    }

    if (timeEndMissing) {
      setError(PRIMARY_TIME_END_PATH as Parameters<typeof setError>[0], {
        type: "required",
        message: TIME_END_SUBMISSION_MESSAGE,
      });
    }

    if (resultMissing) {
      setError(PRIMARY_RESULT_PATH as Parameters<typeof setError>[0], {
        type: "required",
        message: RESULT_SUBMISSION_MESSAGE,
      });
    }

    const message =
      timeEndMissing && resultMissing
        ? TIME_END_AND_RESULT_SUBMISSION_MESSAGE
        : timeEndMissing
          ? TIME_END_SUBMISSION_MESSAGE
          : RESULT_SUBMISSION_MESSAGE;

    setStepValidationSummary(message);
    setCurrentStep(0);

    const firstMissingPath =
      timeEndMissing
        ? PRIMARY_TIME_END_PATH
        : PRIMARY_RESULT_PATH;

    window.setTimeout(() => {
      focusFieldPath(firstMissingPath);
    }, 0);

    return true;
  }

  function commitActiveFieldValue() {
    const activeElement = document.activeElement;

    if (
      !(activeElement instanceof HTMLInputElement) &&
      !(activeElement instanceof HTMLSelectElement) &&
      !(activeElement instanceof HTMLTextAreaElement)
    ) {
      return;
    }

    if (!activeElement.name) {
      return;
    }

    setValue(activeElement.name as Parameters<typeof setValue>[0], activeElement.value as never, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
  }

  function getNormalizedCurrentValues() {
    const latestValues = cloneFormValues(getValues());

    return cloneFormValues({
      ...latestValues,
      members: latestValues.members.map((member) => applySkipPattern(member)),
    });
  }

  function handleClearDraft() {
    clearDraft();
    const nextRecordValues = initializeAddNewDataValues();
    newRecordValuesRef.current = nextRecordValues;
    reset(applySettingsLocation(nextRecordValues, defaultLocation, true));
    setCurrentStep(0);
    setConfirmClearDraft(false);
    setHasUnsavedDraft(false);
    setSubmitPreviewReady(false);
    setSubmissionId(null);
    setSubmissionStorage(null);
    setSubmissionWarning(null);
    didRestoreDraft.current = false;
  }

  function handleSaveDraftClick() {
    if (isEditMode) {
      return;
    }

    commitActiveFieldValue();
    saveDraft(cloneFormValues(getValues()));
    setHasUnsavedDraft(true);
    setSubmissionId(null);
    setSubmissionStorage(null);
    setSubmissionWarning(null);
  }

  function handleCancelEdit() {
    const discardMessage = isHouseholdEditMode
      ? "Discard unsaved household changes?"
      : "Discard unsaved profile changes?";

    if (isDirty && !window.confirm(discardMessage)) {
      return;
    }

    setHasUnsavedDraft(false);
    navigate(isHouseholdEditMode ? "/households" : "/residents");
  }

  function addHouseholdMember() {
    const members = getValues().members;
    const selectedMember = members.find((member) => member.residentId === editResidentId);
    const familyUnitNo = selectedMember?.familyUnitNo || "FAM-001";
    const familyName = selectedMember?.familyName || "Family A";

    append({
      ...emptyMember,
      residentId: createResidentId(),
      familyUnitAssignment: "existing",
      familyUnitNo,
      familyName,
    }, { shouldFocus: false });
    setValue("household.totalMembers", String(fields.length + 1), {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: true,
    });
  }

  function removeHouseholdMember(index: number) {
    remove(index);

    if (isEditMode) {
      setValue("household.totalMembers", String(Math.max(1, fields.length - 1)), {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: true,
      });
    }
  }

  function applyCurrentStepSkipPatterns() {
    const values = getValues();

    values.members.forEach((member, index) => {
      setValue(`members.${index}`, applySkipPattern(member), {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    });
  }

  function showCurrentStepValidationErrors(stepErrors: CurrentStepValidationError[]) {
    const summary = buildValidationBlockSummary(stepErrors[0]);
    setStepValidationSummary(summary);

    if ((import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV) {
      console.warn("Submit blocked by validation", { summary, errors: stepErrors });
    }

    stepErrors.forEach((error) => {
      setError(error.path as Parameters<typeof setError>[0], {
        type: "current-step",
        message: error.message,
      });
    });

    window.setTimeout(() => {
      focusFieldPath(stepErrors[0]?.path);
    }, 0);
  }

  function getAllStepValidationErrors(values: AddNewDataFormValues) {
    return steps
      .filter((step) => step.id < steps.length - 1)
      .flatMap((step) => validateCurrentStep(step.id, values));
  }

  function focusFirstInvalidField() {
    window.setTimeout(() => {
      const invalidField = document.querySelector<HTMLInputElement | HTMLSelectElement>(
        "[aria-invalid='true'], .border-destructive input, .border-destructive select",
      );
      invalidField?.focus();
      invalidField?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }, 0);
  }

  function focusFieldPath(path: string | undefined) {
    if (!path) {
      return;
    }

    try {
      setFocus(path as Parameters<typeof setFocus>[0]);
    } catch {
      // Fall back to DOM focus below for custom table inputs.
    }

    const escapedPath = typeof CSS !== "undefined" && "escape" in CSS ? CSS.escape(path) : path.replace(/"/g, '\\"');
    const input = document.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${escapedPath}"]`);
    input?.focus();
    input?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
  }

  function findStepForPath(path: string) {
    if (path === "household.familyRelationshipToHouseholdHead") {
      return 1;
    }

    const memberFieldMatch = /^members\.\d+\.([^.]+)$/.exec(path);

    if (memberFieldMatch) {
      const fieldName = memberFieldMatch[1];
      const matchedStep = [
        [1, ["firstName", "middleName", "lastName", "relationshipToHead", "sex", "reportedAge", "dateOfBirthUnknown", "dateOfBirth", "placeOfBirth", "nationality", "nationalityOther", "civilStatus", "religion", "ethnicity", "highestEducation", "currentlyEnrolled", "schoolLevel", "placeOfSchool", "reasonNotAttendingSchool", "reasonNotAttendingSchoolOther"]],
        [2, ["monthlyIncome", "sourceOfIncome", "sourceOfIncomeOther", "workStatus", "placeOfWork", "placeOfDelivery", "placeOfDeliveryOther", "birthAttendant", "birthAttendantOther", "immunization", "livingChildren", "livingChildrenAlive", "familyPlanningUse", "familyPlanningUseOther", "fpMethodSource", "fpMethodSourceOther", "intentionToUseFp", "intentionToUseFpDetail"]],
        [3, ["healthInsurance", "healthInsuranceOther", "facilityVisitedPast12Months", "facilityVisitedPast12MonthsOther", "healthFacilityVisitReason", "healthFacilityVisitReasonOther", "disability", "soloParentStatus", "registeredSeniorCitizen", "registeredVoterBarangay", "lengthOfStayInBarangay", "previousResidenceFiveYears", "previousResidenceSixMonths", "residentType"]],
        [4, ["dateOfTransfer", "reasonLeavingPreviousResidence1", "reasonLeavingPreviousResidence1Other", "reasonLeavingPreviousResidence2", "reasonLeavingPreviousResidence2Other", "reasonLeavingPreviousResidence3", "reasonLeavingPreviousResidence3Other", "returnToPreviousResidence", "returnToPreviousResidenceDetail", "transferReason1", "transferReason1Other", "transferReason2", "transferReason2Other", "transferReason3", "transferReason3Other", "durationOfStayCurrentBarangay", "durationOfStayCurrentBarangayDetail", "hasValidCtc", "ctcIssuedInBarangay", "skillsTrainingInterest", "skills", "skillsOther"]],
      ].find(([, fieldsForStep]) => (fieldsForStep as string[]).includes(fieldName));

      return typeof matchedStep?.[0] === "number" ? matchedStep[0] : 1;
    }

    if (path.startsWith("householdQuestions.")) {
      return 5;
    }

    return 0;
  }

  function buildValidationBlockSummary(error: CurrentStepValidationError | undefined) {
    if (!error) {
      return "Please complete all required fields before submitting.";
    }

    const stepId = findStepForPath(error.path);
    const stepTitle = steps.find((step) => step.id === stepId)?.title ?? "Current page";
    const memberMatch = /^members\.(\d+)\.([^.]+)$/.exec(error.path);
    const householdMatch = /^household\.([^.]+)$/.exec(error.path);
    const householdQuestionMatch = /^householdQuestions\.([^.]+)$/.exec(error.path);
    const fieldName =
      memberMatch?.[2] ?? householdMatch?.[1] ?? householdQuestionMatch?.[1] ?? error.path;
    const fieldLabel = memberFieldDisplayLabels[fieldName as MemberFieldName] ?? toDisplayLabel(fieldName);
    const lines = ["Submit blocked by:", stepTitle];

    if (memberMatch) {
      lines.push(`Line ${Number(memberMatch[1]) + 1}`);
    }

    lines.push(fieldLabel, `Reason: ${getFriendlySubmitReason(error.message)}`);

    return lines.join("\n");
  }

  const lastSavedLabel = lastSavedAt
    ? `Draft saved ${formatDraftSavedAt(lastSavedAt)}`
    : "Autosaves every 10 seconds";

  if (isEditMode && (isEditLoading || editLoadError || !editContext)) {
    return (
      <section className="space-y-4">
        <PageHeader
          icon={ClipboardPlus}
          title={isHouseholdEditMode ? "Edit Household" : "Edit Individual Profile"}
          description="Loading the existing canonical household record."
        />
        <Card>
          <CardContent className="space-y-4 p-6">
            <p className="text-sm font-semibold text-slate-700">
              {editLoadError ? `Unable to open editor: ${editLoadError}` : "Loading submitted household..."}
            </p>
            {editLoadError ? (
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  navigate(isHouseholdEditMode ? "/households" : "/residents")
                }
              >
                Cancel
              </Button>
            ) : null}
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <PageHeader
        icon={ClipboardPlus}
        title={
          isHouseholdEditMode
            ? "Edit Household"
            : isIndividualEditMode
              ? "Edit Individual Profile"
              : "Add New Data"
        }
        description={
          isHouseholdEditMode
            ? "Edit the complete existing household record using the full RBIM questionnaire."
            : isIndividualEditMode
              ? "Correct this resident or add a missing member to the existing canonical household."
              : "Encode the familiar RBIM front page and household member pages as an offline desktop draft."
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {isEditMode && editContext ? (
              <>
                {isIndividualEditMode ? (
                  <PageHeaderChip
                    label={`Resident: ${editContext.selectedResidentName}`}
                  />
                ) : null}
                <PageHeaderChip
                  label={`Household: ${editContext.householdCode}`}
                />
                {isIndividualEditMode ? (
                  <PageHeaderChip label={editContext.familyName} />
                ) : null}
              </>
            ) : (
              <>
                {draft ? <PageHeaderChip label="Draft restored" /> : null}
                <PageHeaderChip icon={Clock3} label={lastSavedLabel} />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-white/25 bg-white/[0.12] text-white hover:bg-white/20 hover:text-white"
                  onClick={() => setConfirmClearDraft(true)}
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                  Clear Draft
                </Button>
              </>
            )}
          </div>
        }
      />

      <Card className="rounded-[14px] border-[#d8e0ec] bg-white shadow-[0_8px_22px_rgba(15,23,42,0.05)]">
        <CardContent className="p-2.5">
          <div className="grid min-w-0 gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
            {steps.map((step) => (
              <button
                key={step.id}
                className={cn(
                  "flex h-[70px] min-w-0 items-center gap-2 rounded-[10px] border border-[#d8e0ec] bg-[#f8fafc] p-2.5 text-left transition hover:border-emerald-500",
                  currentStep === step.id && "border-blue-500 bg-blue-50 shadow-sm ring-1 ring-blue-100",
                )}
                type="button"
                onClick={() => void goToStep(step.id)}
              >
                <span
                  className={cn(
                    "grid size-[30px] shrink-0 place-items-center rounded-full border text-xs font-semibold",
                    currentStep === step.id
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "bg-white text-muted-foreground",
                  )}
                >
                  {step.id + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block whitespace-nowrap text-[14px] font-bold leading-[14px] tracking-[-0.02em] text-[#081632]">
                    {step.title}
                  </span>
                  <span className="mt-0.5 block text-[11px] leading-[11px] text-muted-foreground">{step.description}</span>
                </span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <form
        className="flex min-w-0 flex-col gap-4"
        onSubmit={(event) => {
          // Explicit Review submission only.
          // Native form submission must never save or navigate.
          event.preventDefault();
        }}
      >
        <div className="min-w-0 space-y-4 overflow-x-hidden">
          {stepValidationSummary && (
            <div className="whitespace-pre-line rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm font-semibold text-destructive">
              {stepValidationSummary}
            </div>
          )}

          {currentStep === 0 && (
            <fieldset disabled={isIndividualEditMode && isValidSubmissionTimeEnd(currentValues.interviews?.[0]?.timeEnd) && hasSubmissionResult(currentValues.interviews?.[0]?.result)}>
              <RbimFrontPageStep
                register={register}
                values={currentValues}
                errors={errors}
                locationLocked={locationLocked || isIndividualEditMode}
                setValue={setValue}
              />
            </fieldset>
          )}

          {currentStep === 1 && (
            <RbimDemographicPagesStep
              append={append}
              clearErrors={clearErrors}
              errors={errors}
              family={currentValues.household.family}
              fields={fields}
              register={register}
              remove={removeHouseholdMember}
              resetField={resetField}
              setValue={setValue}
              submitCount={submitCount}
              touchedFields={touchedFields}
              ageReferenceDate={ageReferenceDateValue}
              watchedMembers={watchedMembers}
              addMemberLabel={isEditMode ? "Add Household Member" : "Add Line"}
              minimumMemberCount={
                isIndividualEditMode
                  ? editContext?.initialMemberCount ?? 1
                  : 1
              }
              onAddMember={isEditMode ? addHouseholdMember : undefined}
            />
          )}

          {currentStep === 2 && (
            <RbimEconomicHealthPagesStep
              errors={errors}
              fields={fields}
              register={register}
              setValue={setValue}
              watchedMembers={watchedMembers}
            />
          )}

          {currentStep === 3 && (
            <RbimHealthMigrationPagesStep
              errors={errors}
              fields={fields}
              register={register}
              setValue={setValue}
              watchedMembers={watchedMembers}
            />
          )}

          {currentStep === 4 && (
            <RbimMigrationSkillsPagesStep
              errors={errors}
              fields={fields}
              register={register}
              setValue={setValue}
              watchedMembers={watchedMembers}
            />
          )}

          {currentStep === 5 && (
            <fieldset>
              <RbimHouseholdQuestionsStep
                errors={errors}
                family={currentValues.household.family}
                register={register}
                setValue={setValue}
                values={currentValues.householdQuestions}
              />
            </fieldset>
          )}

          {currentStep === steps.length - 1 && (
            <ReviewSubmitStep values={currentValues} />
          )}
        </div>

        <div className="sticky bottom-0 z-20 flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-[14px] border border-[#d8e0ec] bg-white/95 px-4 py-3 shadow-[0_-10px_28px_-18px_rgba(15,23,42,0.35)] backdrop-blur">
          <div className="flex min-w-[260px] flex-1 items-center gap-2 text-sm text-muted-foreground">
            <FileCheck2 className="size-4 text-primary" aria-hidden="true" />
            <span className="min-w-0">
              {submissionId
                ? submissionStorage === "browser-fallback"
                  ? `Saved cleaned RBIM submission ${submissionId} in browser storage.`
                  : `Saved cleaned RBIM submission ${submissionId}.`
                : submitPreviewReady
                  ? "Cleaned review is ready. SQLite saving is available in the desktop runtime."
                  : "Use the page tabs to continue encoding the RBIM census form."}
            </span>
          </div>
          {submissionWarning && (
            <p className="basis-full text-xs font-semibold text-red-700" role="alert">
              Save failed: {submissionWarning} The form and autosaved draft were preserved. Use Retry Submission to try again.
            </p>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            {isEditMode ? (
              <Button type="button" variant="outline" disabled={isSubmittingRecord} onClick={handleCancelEdit}>
                Cancel
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                disabled={isSubmittingRecord}
                onClick={handleSaveDraftClick}
              >
                Save Draft
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              disabled={currentStep === 0}
              onClick={() => setCurrentStep((step) => clampStep(step - 1))}
            >
              Previous
            </Button>
            {currentStep < steps.length - 1 ? (
              <Button type="button" onClick={goToNextStep}>
                Next
              </Button>
            ) : (
              <Button
                type="button"
                disabled={isSubmittingRecord}
                onClick={submitFromReview}
              >
                <CheckCircle2 className="size-4" aria-hidden="true" />
                {isSubmittingRecord
                  ? "Saving..."
                  : isEditMode
                    ? submissionWarning ? "Retry Save Changes" : "Save Changes"
                    : submissionWarning ? "Retry Submission" : "Submit"}
              </Button>
            )}
          </div>
        </div>
      </form>

      {!isEditMode && confirmClearDraft && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-slate-950/45 px-4 backdrop-blur-sm"
          onMouseDown={() => setConfirmClearDraft(false)}
        >
          <div
            className="w-full max-w-md rounded-xl border bg-card p-5 shadow-2xl"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="clear-draft-title"
            aria-describedby="clear-draft-description"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="grid size-11 shrink-0 place-items-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                <AlertTriangle className="size-5" aria-hidden="true" />
              </div>
              <div>
                <h2 id="clear-draft-title" className="text-base font-semibold">
                  Clear autosaved draft?
                </h2>
                <p id="clear-draft-description" className="mt-2 text-sm leading-6 text-muted-foreground">
                  This removes the locally autosaved Add New Data draft and resets the wizard to
                  Step 1. No SQLite records will be changed.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setConfirmClearDraft(false)}>
                Cancel
              </Button>
              <Button type="button" className="bg-amber-600 text-white hover:bg-amber-700" onClick={handleClearDraft}>
                Clear Draft
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const memberFieldDisplayLabels: Record<string, string> = {
  firstName: "Q1 First Name",
  middleName: "Q1 Middle Name",
  lastName: "Q1 Surname",
  relationshipToHead: "Q2 Relationship to Household Head",
  sex: "Q3 Sex",
  reportedAge: "Q4 Reported Age",
  dateOfBirthUnknown: "Q5 Date of Birth Unknown",
  dateOfBirth: "Q5 Date of Birth",
  placeOfBirth: "Q6 Place of Birth",
  nationality: "Q7 Nationality",
  nationalityOther: "Q7 Nationality Detail",
  civilStatus: "Q8 Marital Status",
  religion: "Q9 Religion",
  ethnicity: "Q10 Ethnicity",
  highestEducation: "Q11 Highest Level of Education",
  currentlyEnrolled: "Q12 Currently Enrolled",
  schoolLevel: "Q13 School Level",
  placeOfSchool: "Q14 Place of School",
  reasonNotAttendingSchool: "Q15 Reason for Not Attending School",
  reasonNotAttendingSchoolOther: "Q15 Other Reason Detail",
  monthlyIncome: "Q16 Monthly Income",
  sourceOfIncome: "Q17 Source of Income",
  sourceOfIncomeOther: "Q17 Source of Income Detail",
  workStatus: "Q18 Status of Work / Business",
  placeOfWork: "Q19 Place of Work / Business",
  placeOfDelivery: "Q20 Place of Delivery",
  placeOfDeliveryOther: "Q20 Place of Delivery Detail",
  birthAttendant: "Q21 Birth Attendant",
  birthAttendantOther: "Q21 Birth Attendant Detail",
  immunization: "Q22 Immunization",
  livingChildren: "Q23 Total Pregnancies",
  livingChildrenAlive: "Q23 Living Children",
  familyPlanningUse: "Q24 Family Planning Use",
  familyPlanningUseOther: "Q24 Family Planning Detail",
  fpMethodSource: "Q25 Source of FP Method",
  fpMethodSourceOther: "Q25 Source of FP Detail",
  intentionToUseFp: "Q26 Intention to Use FP",
  intentionToUseFpDetail: "Q26 FP Method / Reason Detail",
  healthInsurance: "Q27 Health Insurance",
  healthInsuranceOther: "Q27 Health Insurance Detail",
  facilityVisitedPast12Months: "Q28 Facility Visited in the Past 12 Months",
  facilityVisitedPast12MonthsOther: "Q28 Facility Detail",
  healthFacilityVisitReason: "Q29 Reason for Visit in the Health Facility",
  healthFacilityVisitReasonOther: "Q29 Visit Reason Detail",
  disability: "Q30 Disability",
  soloParentStatus: "Q31 Solo Parent",
  registeredSeniorCitizen: "Q32 Registered Senior Citizen",
  registeredVoterBarangay: "Q33 Registered Voter",
  lengthOfStayInBarangay: "Q34 Length of Stay in the Barangay",
  previousResidenceFiveYears: "Q35 Previous Residence - Five Years Ago",
  previousResidenceSixMonths: "Q36 Previous Residence - Six Months Ago",
  residentType: "Q37 Type of Resident",
  dateOfTransfer: "Q38 Date of Transfer",
  reasonLeavingPreviousResidence1: "Q39A Reason for Leaving Previous Residence",
  reasonLeavingPreviousResidence2: "Q39B Reason for Leaving Previous Residence",
  reasonLeavingPreviousResidence3: "Q39C Reason for Leaving Previous Residence",
  returnToPreviousResidence: "Q40 Return to Previous Residence",
  returnToPreviousResidenceDetail: "Q40 Return Date / Detail",
  transferReason1: "Q41A Reason for Transferring to Barangay",
  transferReason2: "Q41B Reason for Transferring to Barangay",
  transferReason3: "Q41C Reason for Transferring to Barangay",
  durationOfStayCurrentBarangay: "Q42 Duration of Stay in Current Barangay",
  durationOfStayCurrentBarangayDetail: "Q42 Duration Date / Detail",
  hasValidCtc: "Q43A CTC Information",
  ctcIssuedInBarangay: "Q43B CTC Information",
  skillsTrainingInterest: "Q44 Skills Development Training",
  skills: "Q45 Skills",
  skillsOther: "Q45 Skills Detail",
  femaleDeathAge: "Q58 Female Death Age",
  childDeathAge: "Q59 Child Death Age",
};

function getFriendlySubmitReason(message: string) {
  if (/invalid code/i.test(message)) {
    return "Please enter a valid code or select from the dropdown.";
  }

  if (/others?/i.test(message) && /specify/i.test(message)) {
    return "Please specify the response for Others.";
  }

  if (/required|complete|missing/i.test(message)) {
    return message || "Please complete the required field before submitting.";
  }

  return message;
}

function toDisplayLabel(value: string) {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function cloneFormValues(values: AddNewDataFormValues): AddNewDataFormValues {
  const householdQuestions = {
    ...defaultAddNewDataValues.householdQuestions,
    ...values.householdQuestions,
  };
  const members = values.members.length > 0
    ? values.members.map((member) => ({ ...emptyMember, ...member }))
    : [{ ...emptyMember }];

  return {
    ...values,
    household: { ...defaultAddNewDataValues.household, ...values.household },
    householdQuestions,
    interviews:
      values.interviews?.length === 2
        ? values.interviews.map((interview, index) => ({
            ...defaultAddNewDataValues.interviews[index],
            ...interview,
          }))
        : defaultAddNewDataValues.interviews.map((interview) => ({ ...interview })),
    encoding: { ...defaultAddNewDataValues.encoding, ...values.encoding },
    members,
  };
}

function createResidentId() {
  const uniqueValue = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  return `res_${uniqueValue}`;
}

function applySettingsLocation(
  values: AddNewDataFormValues,
  location: BarangayLocationSettings | null,
  shouldApply = true,
): AddNewDataFormValues {
  if (!location || !shouldApply) {
    return values;
  }

  return {
    ...values,
    household: {
      ...values.household,
      province: location.province,
      provinceCode: location.provinceCode,
      municipality: location.municipality,
      municipalityCode: location.municipalityCode,
      barangay: location.barangay,
      barangayCode: location.barangayCode,
    },
  };
}

function hasSavedLocation(location: BarangayLocationSettings) {
  return Boolean(
    location.province ||
      location.provinceCode ||
      location.municipality ||
      location.municipalityCode ||
      location.barangay ||
      location.barangayCode,
  );
}

function clampStep(step: number) {
  return Math.min(Math.max(step, 0), steps.length - 1);
}

function formatDraftSavedAt(value: string) {
  const savedDate = new Date(value);

  if (!Number.isFinite(savedDate.getTime())) {
    return "recently";
  }

  return new Intl.DateTimeFormat("en-PH", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(savedDate);
}

function isValidSubmissionTimeEnd(value: unknown) {
  const normalized =
    typeof value === "string"
      ? value.trim()
      : "";

  return /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(normalized);
}

function hasSubmissionResult(value: unknown) {
  return typeof value === "string" && value.trim().length > 0;
}

function getCurrentValues(
  watchedValues: AddNewDataFormValues | undefined,
  fallbackValues: AddNewDataFormValues,
) {
  if (
    !watchedValues?.household ||
    !watchedValues.householdQuestions ||
    !watchedValues.members ||
    !watchedValues.interviews ||
    !watchedValues.encoding
  ) {
    return fallbackValues;
  }

  return watchedValues;
}
